import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import ModalForm from 'assets/components/form/modalForm/ModalForm';
import ThemeModalFormDefault from 'assets/components/form/modalForm/themes/Theme.ModalForm.Default';
import Icon from 'assets/components/icon/Icon';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import useApi from 'assets/hooks/api/useApi';
import { getLocales, Language } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { xor } from 'lodash';
import { useMemo, useRef, useState, useEffect } from 'react';
import { useProductDocumentProvider } from '../../ProductDocument.Hooks';
import { useProductDealerGroupDocumentProvider } from '../ProductDocument.DealerGroupDocuments.Hooks';
import DealerGroup from 'models/dealerManagement/dealerGroup/Model.DealerGroup';
import { preventDefaultBehavior } from 'assets/utils/dom/Events';

export interface ProductDealerGroupDocumentAddForm {
  formFile: File;
  translatedFile: File;
  dealerGroups: number[];
  productId: number;
  productDocumentId: number;
}

export default function ProductDealerGroupDocumentAdd() {
  const { lang } = getLocales();
  const { productDocument } = useProductDocumentProvider();
  const { fullDealerGroupDocumentList, handleUploadDealerGroupDocumentSubmit, setView } =
    useProductDealerGroupDocumentProvider();

  const dealerGroupListApi = useApi({
    action: DealerGroup.list,
    body: {
      productId: productDocument?.productId ? [productDocument.productId] : null,
    },
  });

  const availableDealerGroups = useMemo<DealerGroup[]>(() => {
    const dealerGroupData = dealerGroupListApi.payload?.data;
    if (!dealerGroupData?.length) {
      return [];
    }

    if (fullDealerGroupDocumentList?.length > 0) {
      return dealerGroupData.filter(
        (dealerGroup) => !fullDealerGroupDocumentList.some((doc) => doc.dealerGroupId === dealerGroup.id)
      );
    }

    return dealerGroupData;
  }, [dealerGroupListApi.payload?.data, fullDealerGroupDocumentList]);

  const fileInput = useRef<HTMLInputElement>();
  const [languageIsoCode, setLanguageIsoCode] = useState<{ code: Language | undefined }>({ code: undefined });
  useEffect(() => {
    if (languageIsoCode.code) {
      fileInput?.current.click();
    }
  }, [languageIsoCode]);

  return (
    <ModalForm<ProductDealerGroupDocumentAddForm>
      subcomponents={ThemeModalFormDefault}
      dependencyArray={[productDocument]}
      title={lang.upload}
      default={{ productDocumentId: productDocument?.id, productId: productDocument?.productId }}
      validation={(data) => ({ dealerGroups: data.dealerGroups?.length ? undefined : lang.required })}
      onCancel={() => setView(undefined)}
      onSubmit={(data) => handleUploadDealerGroupDocumentSubmit(data)}
      submitButtonText={lang.upload}
      render={({ form }) => (
        <>
          <input
            ref={fileInput}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => {
              preventDefaultBehavior(event);

              const formFile = event.currentTarget.files[0];
              if (languageIsoCode.code === Language.english) {
                form.update({ formFile });
              } else {
                form.update({ translatedFile: formFile });
              }
              event.currentTarget.value = '';
            }}
            type="file"
            max={1}
            accept="*"
            multiple={false}
            style={{ display: 'none' }}
          />
          <Box style={{ display: 'flex', justifyContent: 'flex-end', padding: Theme.Size.S }}>
            <Button
              class={ThemeButtonCircle}
              style={{ display: 'inline-flex', color: !form.data.formFile ? undefined : Theme.Color.success }}
              media={<Icon class={form.data.formFile ? 'fas-check' : 'fas-upload'} />}
              data={{ code: Language.english }}
              onClick={setLanguageIsoCode}
            >
              {lang.en}
            </Button>
            <Button
              class={ThemeButtonCircle}
              style={{
                display: 'inline-flex',
                color: !form.data.translatedFile ? undefined : Theme.Color.success,
                marginRight: 'auto',
              }}
              media={<Icon class={form.data.translatedFile ? 'fas-check' : 'fas-upload'} />}
              data={{ code: Language.french }}
              onClick={setLanguageIsoCode}
              disabled={!form.data.formFile}
            >
              {lang.fr}
            </Button>
            <Button
              class={{ button: ThemeButtonCircle }}
              media={
                form.data.dealerGroups?.length === availableDealerGroups?.length
                  ? 'fas-minus-square'
                  : 'fas-check-square'
              }
              onClick={form.update}
              data={{
                dealerGroups:
                  !availableDealerGroups || form.data.dealerGroups?.length === availableDealerGroups?.length
                    ? []
                    : [...availableDealerGroups.map((x) => x.id)],
              }}
              active={form.data.dealerGroups?.length === availableDealerGroups?.length}
              htmlElementProps={{ title: lang.selectAll }}
              render={lang.selectAll}
            />
          </Box>
          <table className={ThemeTableSimple}>
            <thead>
              <tr>
                <th children={lang.dealerGroup} />
                <th children={lang.actions} style={{ textAlign: 'center' }} />
              </tr>
            </thead>
            <tbody>
              <DataMap
                data={availableDealerGroups}
                render={({ data }) => {
                  const isItemSelected = form.data.dealerGroups?.find((it) => it === data.id);

                  return (
                    <tr key={data.id}>
                      <td children={data.displayInfo.title} />
                      <td style={{ textAlign: 'center' }}>
                        <Button
                          data={data}
                          onClick={() => {
                            form.update({ dealerGroups: xor(form.data.dealerGroups, [data.id]) });
                          }}
                          class={ThemeButtonCircle}
                          media={<Icon class={isItemSelected ? 'fas-check-square' : 'far-square'} />}
                          htmlElementProps={{ title: isItemSelected ? lang.unselect : lang.select }}
                        />
                      </td>
                    </tr>
                  );
                }}
              />
            </tbody>
          </table>
        </>
      )}
    />
  );
}
